import { preloadImage, onDocumentReady } from '@theme/utilities';

/** @type {Record<string, number>} */
const cache = {};

const shopName = window.Shopify.shop;
// We want to encode shop-url to an integer dividable by Theme.placeholder.general.length
// to get a starting number that is unique to the shop
const shopPlaceholderSeed = shopName.split('-').reduce((acc, char) => acc + char.charCodeAt(0), 0);
const counters = {
  product: 0,
  general: shopPlaceholderSeed % Theme.placeholders.general.length,
};

const isEditorRequest = window.Shopify.visualPreviewMode || window.Shopify.designMode;

if (isEditorRequest) {
  onDocumentReady(() => {
    const placeholderUrls = Object.values(Theme.placeholders).flat();

    for (const url of placeholderUrls) {
      preloadImage(url);
    }
  });
}

class PlaceholderImage extends HTMLElement {
  /**
   * Create a responsive image element with srcset for optimal loading
   * @param {string} baseUrl - The base URL of the placeholder image
   * @param {string} blockId - The block identifier
   * @param {number} [sectionIndex] - Optional section index for lazy loading
   * @returns {HTMLImageElement}
   */
  createResponsiveImage(baseUrl, blockId, sectionIndex) {
    const imageElement = document.createElement('img');
    
    // Generate responsive image URLs with different widths
    // Shopify CDN supports width parameters for optimization
    const widths = [380, 540, 720, 900, 1080];
    const srcsetParts = widths.map(width => {
      // Try to add width parameter to Shopify CDN URL
      const url = baseUrl.includes('?') 
        ? `${baseUrl}&width=${width}` 
        : `${baseUrl}?width=${width}`;
      return `${url} ${width}w`;
    });
    
    // Set the default src to a medium size (540px)
    imageElement.src = baseUrl.includes('?') 
      ? `${baseUrl}&width=540` 
      : `${baseUrl}?width=540`;
    
    // Add srcset for responsive loading
    imageElement.srcset = srcsetParts.join(', ');
    
    // Set sizes attribute - adjust based on your layout
    // This example assumes the image takes up different widths at different breakpoints
    imageElement.sizes = '(min-width: 1200px) 380px, (min-width: 750px) 50vw, 100vw';
    
    // Set dimensions to prevent layout shift
    imageElement.width = 380;
    imageElement.height = 504;
    
    imageElement.alt = Theme.translations.placeholder_image || 'Placeholder Image';
    
    return imageElement;
  }

  connectedCallback() {
    if (this.hasChildNodes()) return;

    const type = /** @type {'product' | 'general'} */ (this.dataset.type);
    const blockId = this.dataset.blockId;
    if (!type || !(type in counters) || !blockId) return;

    const cached = cache[blockId];
    if (cached != null && Theme.placeholders[type][cached]) {
      const image = Theme.placeholders[type][cached];
      this.appendChild(this.createResponsiveImage(image, blockId));
      return;
    }

    const counter = counters[type] || 0;
    counters[type] = (counter + 1) % Theme.placeholders[type].length;

    const image = Theme.placeholders[type][counter];
    if (!image) return;

    const sectionElement = this.closest('section');
    const sectionIndex = sectionElement
      ? Array.from(sectionElement.parentElement?.children ?? []).indexOf(sectionElement)
      : -1;

    const imageElement = this.createResponsiveImage(image, blockId, sectionIndex);

    if (sectionIndex === -1 || sectionIndex > 3 || isEditorRequest) {
      imageElement.loading = 'lazy';
    }

    this.appendChild(imageElement);
    cache[blockId] = counter;
  }
}

if (!customElements.get('placeholder-image')) {
  customElements.define('placeholder-image', PlaceholderImage);
}
